import { BehaviorSubject } from 'rxjs';
import environment from '../environment';
import { UserRoleEnum } from '../models/enums/UserRoleEnum';
import UserLogin from '../models/UserLogin';
import api from './api';
import { buildAppPath, normalizeAppPath } from '../utils/slug';

const currentUserSubject = new BehaviorSubject<UserLogin | null>(null);

if (localStorage.getItem(environment.userStorage) != null) {
    const user = localStorage.getItem(environment.userStorage);
    currentUserSubject.next(user ? JSON.parse(user) : null);
}

export const authenticationService = {
    login,
    logout,
    removeUserLogged,
    currentUser: currentUserSubject,
    get currentUserValue (): any { return currentUserSubject.value },
    currentUserRoles,
    hasRole,
    hasAnyRole,
    userRole,
    showRole,
    canChange,
    isAdmin,
    isReadOnly,
    isAuxSec,
    isRoutine,
    isYouth,
    isAuxCampaign,
    isFinance,
    accessSecretaryRoles,
    accessAdminRoles,
    accessMembersRoles,
    canChangeYouth,
    accessCampaignRoles,
    accessAuxCampaignRoles,
    canChangeCampaign,
    accessYouthRoles,
    accessAdminYouthRoles,
    accessRoutinesRoles,
    accessFinanceRoles,
    accessEventRoles
};

function currentUserRoles(): UserRoleEnum[] {
    return currentUserSubject.value?.roles ?? [];
}

function hasRole(role: UserRoleEnum) {
    return currentUserRoles().includes(role);
}

function hasAnyRole(roles: UserRoleEnum[]) {
    return roles.some((role) => hasRole(role));
}

async function login(username: string, password: string) {
    const response = await api.post('login', { 'username': username, 'password': password });

    if (response.data.success) {
        localStorage.setItem(environment.userStorage, JSON.stringify(response.data.item));
        currentUserSubject.next(response.data.item);

        return response.data.item;
    }
}

async function logout(redirectTo: any) {
    await api.post('logout', { token: currentUserSubject.value?.refreshToken });
    removeUserLogged();

    const normalizedRedirectTo = (redirectTo && typeof(redirectTo) == 'string')
        ? normalizeAppPath(redirectTo)
        : '';

    const loginPath = `/entrar${normalizedRedirectTo ? `?redirecionar=${encodeURIComponent(normalizedRedirectTo)}` : ''}`;
    window.location.href = buildAppPath(loginPath);
}

async function removeUserLogged() {
    localStorage.removeItem(environment.userStorage);
    localStorage.removeItem(`${environment.storage}member-congregation`);
    currentUserSubject.next(null);
}

function userRole() {
    return showRole(currentUserSubject.value?.roles);
}

function showRole(role: string | string[] | undefined): string {
    if (Array.isArray(role)) {
        return role.length > 0
            ? role.map((item) => item).join(', ')
            : "Sem Perfil";
    }

    return "Sem Perfil";
}

export function canChange() {
    return hasAnyRole([UserRoleEnum.ADMIN, UserRoleEnum.SECRETARY]);
}

export function canChangeFinance() {
    return hasAnyRole([...accessAdminRoles(), UserRoleEnum.FINANCE]);
}

export function isAdmin() {
    return hasRole(UserRoleEnum.ADMIN);
}

export function isReadOnly() {
    if (!currentUserSubject.value) return true;
    return hasAnyRole([UserRoleEnum.READ, UserRoleEnum.ROUTINE]);
}

export function isAuxSec() {
    return hasRole(UserRoleEnum.AUX_SECRETARY);
}

export function isRoutine() {
    return hasRole(UserRoleEnum.ROUTINE);
}

function isYouth() {
    return hasAnyRole([UserRoleEnum.YOUTH, UserRoleEnum.AUX_YOUTH]);
}

function isAuxCampaign() {
    return hasRole(UserRoleEnum.AUX_CAMPAIGN);
}

function isFinance() {
    return hasAnyRole([...accessAdminRoles(), UserRoleEnum.FINANCE]);
}

function accessSecretaryRoles() {
    return [...accessAdminRoles(), UserRoleEnum.SECRETARY, UserRoleEnum.READ];
}

function accessAdminRoles() {
    return [UserRoleEnum.ADMIN];
}

function accessMembersRoles() {
    return [...accessAdminRoles(), UserRoleEnum.SECRETARY, UserRoleEnum.AUX_SECRETARY, UserRoleEnum.READ];
}

function canChangeYouth() {
    return canChange() || isYouth();
}

function accessCampaignRoles() {
    return [...accessAdminRoles(), UserRoleEnum.AUX_CAMPAIGN, UserRoleEnum.READ, UserRoleEnum.FINANCE];
}

function accessAuxCampaignRoles() {
    return [UserRoleEnum.AUX_CAMPAIGN];
}

function canChangeCampaign() {
    return canChangeFinance();
}

function accessYouthRoles() {
    return [...accessAdminYouthRoles(), UserRoleEnum.AUX_YOUTH];
}

function accessAdminYouthRoles() {
    return [...accessAdminRoles(), UserRoleEnum.YOUTH];
}

function accessRoutinesRoles() {
    return [...accessSecretaryRoles(), UserRoleEnum.ROUTINE];
}

function accessFinanceRoles() {
    return [...accessAdminRoles(), UserRoleEnum.FINANCE];
}

function accessEventRoles() {
    return [...accessSecretaryRoles(), UserRoleEnum.FINANCE];
}
